import { NavLink, Outlet } from 'react-router-dom';
import { useTheme } from '@/app/theme';
import { useAuth } from '@/lib/auth/auth-store';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/', label: 'Live', end: true },
  { to: '/activity', label: 'Activity' },
  { to: '/employees', label: 'Employees' },
  { to: '/health', label: 'System' },
  { to: '/settings', label: 'Settings' },
];

export function AppShell() {
  const { theme, toggle } = useTheme();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/*
       * Sticky and translucent: the live map is the tallest page in the app and
       * an admin scrolling to the detail panel should not have to scroll back
       * up to change screens.
       */}
      <header className="sticky top-0 z-[1100] border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-4 px-4">
          <span className="flex items-center gap-2">
            <img src="/favicon.svg" alt="" className="h-6 w-6" aria-hidden />
            <span className="font-semibold tracking-tight">Yuna</span>
          </span>

          <nav className="flex gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'rounded-lg px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-muted font-medium text-foreground'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-sm text-muted-foreground sm:inline">{user?.name}</span>
            <button
              type="button"
              onClick={toggle}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              className="grid h-8 w-8 place-items-center rounded-lg border border-border transition-colors hover:bg-muted"
            >
              {theme === 'dark' ? (
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-lg border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Wider than the old 7xl: the live map, the roster and the detail panel
          have to sit side by side on a desktop without any of the three being
          squeezed into uselessness. */}
      <main className="mx-auto max-w-[1600px] px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
